import React, { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, Coins, Pencil, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const CONFIG_FIELDS = [
  ["five_star_percent", "5-star reward (%)", "0.01"],
  ["four_star_percent", "4-star reward (%)", "0.01"],
  ["one_to_three_star_percent", "1–3 star reward (%)", "0.01"],
  ["minimum_redemption_points", "Minimum points to redeem", "0.01"],
  ["redemption_delay_hours", "Redemption delay (hours)", "1"],
  ["maximum_discount_percent", "Maximum order discount (%)", "0.01"],
  ["expiry_days", "Points expiry (days)", "1"],
  ["birthday_bonus_points", "Birthday bonus points", "1"],
];

const points = (value) => Number(value || 0).toLocaleString("en-IN", {
  maximumFractionDigits: 2,
});

const orderLabel = (orderId) => (
  orderId ? `Order #${String(orderId).slice(0, 8)}` : "Order unavailable"
);

const emptyTier = {
  name: "",
  minimum_points: 1,
  redemption_percent: 100,
  discount_percent: 0,
  perks: "",
};

export const EiraPointsAdminTab = () => {
  const [config, setConfig] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [tierForm, setTierForm] = useState(emptyTier);
  const [editingTierId, setEditingTierId] = useState("");
  const [savingTier, setSavingTier] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sort, setSort] = useState({ key: "balance", direction: "desc" });

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [configResponse, dashboardResponse, tierResponse] = await Promise.all([
        api.get("/admin/eira-points-config"),
        api.get("/admin/eira-points-dashboard"),
        api.get("/admin/eira-points-tiers"),
      ]);
      setConfig(configResponse.data);
      setCustomers(dashboardResponse.data.customers || []);
      setTiers(tierResponse.data || []);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await api.put("/admin/eira-points-config", config);
      setConfig(response.data);
      toast.success("Eira Points settings saved");
      await load({ refresh: true });
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSaving(false);
    }
  };

  const updateNumber = (key, value) => {
    setConfig((current) => ({ ...current, [key]: Number(value || 0) }));
  };

  const saveTier = async (event) => {
    event.preventDefault();
    setSavingTier(true);
    try {
      const payload = {
        ...tierForm,
        minimum_points: Number(tierForm.minimum_points),
        redemption_percent: Number(tierForm.redemption_percent),
        discount_percent: Number(tierForm.discount_percent),
        perks: tierForm.perks.split("\n").map((perk) => perk.trim()).filter(Boolean),
      };
      if (editingTierId) {
        await api.put(`/admin/eira-points-tiers/${editingTierId}`, payload);
        toast.success("Tier updated");
      } else {
        await api.post("/admin/eira-points-tiers", payload);
        toast.success("Tier created");
      }
      setTierForm(emptyTier);
      setEditingTierId("");
      await load({ refresh: true });
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setSavingTier(false);
    }
  };

  const editTier = (tier) => {
    setEditingTierId(tier.id);
    setTierForm({ ...tier, perks: (tier.perks || []).join("\n") });
  };

  const deleteTier = async (tier) => {
    if (!window.confirm(`Delete ${tier.name}? Customers will recalculate immediately.`)) return;
    try {
      await api.delete(`/admin/eira-points-tiers/${tier.id}`);
      toast.success("Tier deleted");
      await load({ refresh: true });
    } catch (error) {
      toast.error(formatError(error));
    }
  };

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedCustomers = [...customers].sort((left, right) => {
    const leftValue = sort.key === "customer" ? left.name || left.email : Number(left[sort.key] || 0);
    const rightValue = sort.key === "customer" ? right.name || right.email : Number(right[sort.key] || 0);
    const comparison = typeof leftValue === "string"
      ? leftValue.localeCompare(rightValue)
      : leftValue - rightValue;
    return sort.direction === "asc" ? comparison : -comparison;
  });

  const SortHeader = ({ label, sortKey }) => {
    const active = sort.key === sortKey;
    return (
      <th className="px-3 py-3">
        <button
          type="button"
          onClick={() => toggleSort(sortKey)}
          className="inline-flex items-center gap-1 font-semibold hover:text-[#2D5A47]"
          aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
          data-testid={`sort-customer-points-${sortKey}`}
        >
          {label}
          {active && sort.direction === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
        </button>
      </th>
    );
  };

  return (
    <div className="w-full min-w-0 max-w-7xl" data-testid="eira-points-admin-tab">
      <div
        className={[
          "flex flex-col justify-between gap-4 border-b border-[#E2DCD2] pb-6",
          "sm:flex-row sm:items-end",
        ].join(" ")}
      >
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">
            Loyalty programme
          </p>
          <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1E2A24]">
            Eira Points
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[#64748B]">
            Manage reward rules and review customer balances without changing their records.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load({ refresh: true })}
          disabled={refreshing}
          className={[
            "inline-flex w-fit items-center gap-2 border border-[#2D5A47] px-4 py-2 text-sm",
            "font-semibold text-[#2D5A47] hover:bg-[#EBF2EE] disabled:opacity-50",
          ].join(" ")}
          data-testid="refresh-eira-points-dashboard"
        >
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh dashboard"}
        </button>
      </div>

      <section
        className="mt-6 border border-[#E2DCD2] bg-white p-6"
        data-testid="eira-points-settings"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-serif text-2xl font-semibold text-[#1E2A24]">
              Settings
            </h3>
            <p className="mt-1 text-sm text-[#64748B]">
              New reward entries use the expiry rule saved here. Set expiry to 0 to keep new
              points valid indefinitely.
            </p>
          </div>
          {config ? (
            <button
              type="button"
              onClick={() => setConfig({ ...config, enabled: !config.enabled })}
              className={`w-fit px-4 py-2 text-sm font-semibold text-white ${
                config.enabled ? "bg-emerald-700" : "bg-[#7F1D1D]"
              }`}
              aria-pressed={config.enabled}
              data-testid="toggle-eira-points"
            >
              {config.enabled ? "Points enabled" : "Points disabled"}
            </button>
          ) : null}
        </div>

        {loading || !config ? (
          <p
            className="mt-5 text-sm text-[#64748B]"
            data-testid="eira-points-settings-loading"
          >
            Loading Eira Points settings…
          </p>
        ) : (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CONFIG_FIELDS.map(([key, label, step]) => (
                <label key={key} className="text-sm font-medium text-[#1E2A24]">
                  {label}
                  <input
                    type="number"
                    min="0"
                    step={step}
                    value={config[key] ?? 0}
                    onChange={(event) => updateNumber(key, event.target.value)}
                    className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2"
                    data-testid={`eira-points-${key}`}
                  />
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className={[
                "mt-6 inline-flex items-center gap-2 bg-[#2D5A47] px-5 py-2.5 text-sm",
                "font-semibold text-white disabled:opacity-50",
              ].join(" ")}
              data-testid="save-eira-points-config"
            >
              <Save size={15} />
              {saving ? "Saving…" : "Save Eira Points settings"}
            </button>
          </>
        )}
      </section>

      <section className="mt-6 border border-[#E2DCD2] bg-white p-6" data-testid="eira-tier-management">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8C6D46]">Loyalty tiers</p>
            <h3 className="mt-2 font-serif text-2xl text-[#1E2A24]">Tier Management</h3>
            <p className="mt-1 text-sm text-[#64748B]">Member is built in for every customer below your first tier.</p>
          </div>
          {editingTierId && <button type="button" onClick={() => { setEditingTierId(""); setTierForm(emptyTier); }} className="text-sm font-semibold text-[#64748B]" data-testid="cancel-tier-edit">Cancel edit</button>}
        </div>
        <form onSubmit={saveTier} className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="eira-tier-form">
          <label className="text-sm font-medium">Name<input required value={tierForm.name} onChange={(event) => setTierForm({ ...tierForm, name: event.target.value })} className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2" data-testid="tier-name-input" /></label>
          <label className="text-sm font-medium">Min points<input required type="number" min="1" value={tierForm.minimum_points} onChange={(event) => setTierForm({ ...tierForm, minimum_points: event.target.value })} className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2" data-testid="tier-minimum-points-input" /></label>
          <label className="text-sm font-medium">Redemption cap %<input required type="number" min="0" max="100" value={tierForm.redemption_percent} onChange={(event) => setTierForm({ ...tierForm, redemption_percent: event.target.value })} className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2" data-testid="tier-redemption-percent-input" /></label>
          <label className="text-sm font-medium">Checkout discount %<input required type="number" min="0" max="100" value={tierForm.discount_percent} onChange={(event) => setTierForm({ ...tierForm, discount_percent: event.target.value })} className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2" data-testid="tier-discount-percent-input" /></label>
          <label className="text-sm font-medium">Perks (one per line)<textarea value={tierForm.perks} onChange={(event) => setTierForm({ ...tierForm, perks: event.target.value })} rows={2} className="mt-1 block w-full border border-[#E2DCD2] px-3 py-2" data-testid="tier-perks-input" /></label>
          <button type="submit" disabled={savingTier} className="inline-flex w-fit items-center gap-2 bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="save-eira-tier">{editingTierId ? <Save size={14} /> : <Plus size={14} />}{savingTier ? "Saving…" : editingTierId ? "Save tier" : "Create tier"}</button>
        </form>
        <div className="mt-6 space-y-2" data-testid="eira-tier-list">
          <div className="flex items-center justify-between border border-[#D9C3A5] bg-[#FDFBF7] p-3 text-sm"><span><strong>Member</strong> · 0 points</span><span>{config?.maximum_discount_percent ?? 100}% redemption cap</span></div>
          {tiers.map((tier) => <div key={tier.id} className="flex flex-wrap items-center justify-between gap-3 border border-[#E2DCD2] p-3" data-testid={`eira-tier-row-${tier.id}`}><div><p className="font-semibold text-[#1E2A24]">{tier.name} · {tier.minimum_points} points</p><p className="mt-1 text-xs text-[#64748B]">{tier.redemption_percent}% redemption · {tier.discount_percent}% checkout discount{tier.perks?.length ? ` · ${tier.perks.join(", ")}` : ""}</p></div><div className="flex gap-2"><button type="button" onClick={() => editTier(tier)} className="p-2 text-[#2D5A47]" data-testid={`edit-eira-tier-${tier.id}`}><Pencil size={15} /></button><button type="button" onClick={() => deleteTier(tier)} className="p-2 text-red-700" data-testid={`delete-eira-tier-${tier.id}`}><Trash2 size={15} /></button></div></div>)}
        </div>
      </section>

      <section
        className="mt-6 border border-[#E2DCD2] bg-white p-6"
        data-testid="customer-points-dashboard"
      >
        <div className="flex items-center gap-3">
          <Coins size={20} className="text-[#C88A2D]" />
          <div>
            <h3 className="font-serif text-2xl font-semibold text-[#1E2A24]">
              Customer Points Dashboard
            </h3>
            <p className="mt-1 text-sm text-[#64748B]">
              Balances and transaction history are read-only.
            </p>
          </div>
        </div>

        {loading ? (
          <p
            className="mt-6 text-sm text-[#64748B]"
            data-testid="customer-points-loading"
          >
            Loading customer point balances…
          </p>
        ) : customers.length === 0 ? (
          <p
            className="mt-6 text-sm text-[#64748B]"
            data-testid="customer-points-empty"
          >
            No customer accounts yet.
          </p>
        ) : (
          <div className="admin-table-scroll mt-6" data-testid="customer-points-table-wrap">
            <table
              className="min-w-[980px] w-full text-left text-sm"
              data-testid="customer-points-table"
            >
              <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wide text-[#64748B]">
                <tr>
                  <SortHeader label="Customer" sortKey="customer" />
                  <SortHeader label="Balance" sortKey="balance" />
                  <SortHeader label="Earned" sortKey="total_earned" />
                  <SortHeader label="Redeemed" sortKey="total_redeemed" />
                  <SortHeader label="Pending" sortKey="pending_points" />
                  <SortHeader label="Expired" sortKey="expired_points" />
                  <th className="px-3 py-3">History</th>
                </tr>
              </thead>
              <tbody>
                {sortedCustomers.map((customer) => {
                  const expanded = expandedCustomer === customer.id;
                  return (
                    <React.Fragment key={customer.id}>
                      <tr
                        className="border-t border-[#E2DCD2]"
                        data-testid={`customer-points-row-${customer.id}`}
                      >
                        <td className="px-3 py-4">
                          <p className="font-semibold text-[#1E2A24]">
                            {customer.name || "Customer"}
                          </p>
                          <p className="mt-0.5 text-xs text-[#64748B]">{customer.email}</p>
                        </td>
                        <td className="px-3 py-4 font-semibold text-[#2D5A47]">
                          {points(customer.balance)}
                        </td>
                        <td className="px-3 py-4">{points(customer.total_earned)}</td>
                        <td className="px-3 py-4">{points(customer.total_redeemed)}</td>
                        <td className="px-3 py-4">{points(customer.pending_points)}</td>
                        <td className="px-3 py-4">{points(customer.expired_points)}</td>
                        <td className="px-3 py-4">
                          <button
                            type="button"
                            onClick={() => setExpandedCustomer(expanded ? null : customer.id)}
                            className={[
                              "inline-flex items-center gap-1 text-sm font-semibold text-[#C86D42]",
                              "hover:underline",
                            ].join(" ")}
                            aria-expanded={expanded}
                            data-testid={`toggle-customer-points-history-${customer.id}`}
                          >
                            {expanded ? "Hide history" : "View history"}
                            {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                          </button>
                        </td>
                      </tr>
                      {expanded ? (
                        <tr data-testid={`customer-points-history-${customer.id}`}>
                          <td colSpan={7} className="bg-[#FDFBF7] px-5 py-4">
                            {customer.history?.length ? (
                              <div className="space-y-2">
                                {customer.history.map((entry, index) => (
                                  <div
                                    key={`${entry.type}-${entry.created_at}-${index}`}
                                    className={[
                                      "flex flex-wrap items-center justify-between gap-2",
                                      "border-b border-[#E2DCD2] pb-2 text-xs last:border-0",
                                    ].join(" ")}
                                  >
                                    <span className="font-semibold capitalize text-[#1E2A24]">
                                      {entry.type} · {entry.product_name}
                                    </span>
                                    <span className="text-[#64748B]">
                                      {orderLabel(entry.order_id)} · {entry.created_at
                                        ? new Date(entry.created_at).toLocaleString("en-IN")
                                        : "Time unavailable"}
                                    </span>
                                    <span
                                      className={
                                        entry.type === "earned"
                                          ? "text-emerald-700"
                                          : "text-[#C86D42]"
                                      }
                                    >
                                      {entry.type === "earned" ? "+" : "−"}
                                      {points(entry.points)} pts
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-[#64748B]">
                                No points transactions yet.
                              </p>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};